import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { type CompanyPlan, type CompanyModulesConfig, type PixKeyType } from './companyConfigService';

export interface AdminCompanyAddress {
  cidade: string;
  estado: string;
  complemento?: string;
  numero: string;
  bairro: string;
  rua: string;
  cep: string;
}

export interface AdminCompany {
  id: string;
  cnpj: string;
  razaoSocial?: string;
  slug?: string;
  customerPortalUrl?: string;
  whatsappPhone?: string;
  message?: string;
  active: boolean;
  isOpen?: boolean;
  billingBlocked: boolean;
  plan?: CompanyPlan;
  endereco?: AdminCompanyAddress;
  diasFuncionamento?: number[];
  dataInicioUsoPlataforma?: string | null;
  modules?: CompanyModulesConfig;
  createdAt: string;
  updatedAt: string;
}

export type UpdateAdminCompanyPayload = Partial<{
  razaoSocial: string;
  slug: string;
  customerPortalUrl: string;
  whatsappPhone: string;
  message: string;
  active: boolean;
  billingBlocked: boolean;
  plan: CompanyPlan;
  endereco: {
    cidade: string;
    estado: string;
    complemento?: string;
    numero: string;
    bairro: string;
    rua: string;
    cep: string;
  };
  diasFuncionamento: number[];
  dataInicioUsoPlataforma: string | null;
  modules: {
    payments?: {
      enabled: boolean;
      pixKey?: string;
      pixKeyType?: PixKeyType;
    };
    inventory?: {
      enabled: boolean;
    };
  } | null;
}>;

class CompanyAdminService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  async listCompanies(): Promise<AdminCompany[]> {
    try {
      const response: AxiosResponse<{ companies: AdminCompany[] }> = await this.client.get('/admin/companies');
      return response.data?.companies ?? [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar empresas');
    }
  }

  async updateCompany(id: string, payload: UpdateAdminCompanyPayload): Promise<AdminCompany> {
    try {
      const response: AxiosResponse<{ company: AdminCompany }> = await this.client.patch(`/admin/companies/${id}`, payload);
      return response.data.company;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar empresa');
    }
  }
}

export const companyAdminService = new CompanyAdminService();
export default companyAdminService;
